import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Calendar, Clock, Receipt, FileText, User, 
  TrendingUp, Bell, CheckCircle, XCircle, AlertCircle,
  Download, Eye, Plus, Briefcase, Award
} from 'lucide-react';
import hrApiClient from '@/api/hrClient';

export default function EmployeeSelfService() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  
  const [myInfo, setMyInfo] = useState({
    name: userData.name || 'Employee',
    email: userData.email || '',
    department: 'Engineering',
    position: 'Software Engineer',
    joinDate: '2023-01-15',
    employeeId: 'EMP001'
  });
  
  const [leaveBalance, setLeaveBalance] = useState({
    annual: { used: 5, total: 14 },
    sick: { used: 2, total: 14 },
    personal: { used: 1, total: 3 }
  });
  
  const [recentPayslips, setRecentPayslips] = useState([
    { month: 'January 2026', amount: 'RM 8,500', status: 'Paid' },
    { month: 'December 2025', amount: 'RM 8,500', status: 'Paid' },
    { month: 'November 2025', amount: 'RM 8,500', status: 'Paid' }
  ]);
  
  const [pendingRequests, setPendingRequests] = useState([
    { type: 'Leave', description: 'Annual Leave - Feb 15-17', status: 'pending' },
    { type: 'Claim', description: 'Transport Claim - RM 250', status: 'pending' }
  ]);
  
  const [attendance, setAttendance] = useState({
    thisMonth: { present: 18, total: 22 },
    checkInTime: '09:05 AM',
    checkOutTime: '--:--',
    status: 'Present'
  });

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  const fetchEmployeeData = async () => {
    try {
      // Fetch employee's own data
      const response = await hrApiClient.get('/employee/me');
      if (response.data) {
        setMyInfo(response.data);
      }
    } catch (error) {
      console.log('Using default employee data');
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { 
      title: 'Apply Leave', 
      icon: Calendar, 
      color: 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20',
      onClick: () => navigate('/suite/hr/leave/apply')
    },
    { 
      title: 'Submit Claim', 
      icon: Receipt, 
      color: 'bg-green-500/10 text-green-400 hover:bg-green-500/20',
      onClick: () => navigate('/suite/hr/claims/submit')
    },
    { 
      title: 'View Payslips', 
      icon: FileText, 
      color: 'bg-purple-500/10 text-purple-400 hover:bg-purple-500/20',
      onClick: () => navigate('/suite/hr/payslips')
    },
    { 
      title: 'My Profile', 
      icon: User, 
      color: 'bg-orange-500/10 text-orange-400 hover:bg-orange-500/20',
      onClick: () => navigate('/suite/hr/profile')
    }
  ];

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-400" />;
      default: return <AlertCircle className="w-4 h-4 text-yellow-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'approved': return <Badge className="bg-green-500/20 text-green-400 border-0">Approved</Badge>;
      case 'rejected': return <Badge className="bg-red-500/20 text-red-400 border-0">Rejected</Badge>;
      default: return <Badge className="bg-yellow-500/20 text-yellow-400 border-0">Pending</Badge>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">
            Welcome back, {myInfo.name.split(' ')[0]}!
          </h1>
          <p className="text-slate-400 mt-1">
            {myInfo.position} • {myInfo.department}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-4 py-2 rounded-lg ${
            attendance.status === 'Present' 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-slate-700 text-slate-400'
          }`}>
            <span className="text-sm font-medium">
              {attendance.status === 'Present' ? '✓ Checked In' : 'Not Checked In'}
            </span>
            {attendance.checkInTime !== '--:--' && (
              <span className="ml-2 text-xs opacity-75">{attendance.checkInTime}</span>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {quickActions.map((action, idx) => (
          <button
            key={idx}
            onClick={action.onClick}
            className={`p-4 rounded-xl border border-slate-700/50 transition-all duration-200 ${action.color}`}
          >
            <action.icon className="w-6 h-6 mb-2" />
            <span className="text-sm font-medium">{action.title}</span>
          </button>
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Leave Balance */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-400" />
              Leave Balance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-400">Annual Leave</span>
                <span className="text-white">{leaveBalance.annual.total - leaveBalance.annual.used} / {leaveBalance.annual.total} days</span>
              </div>
              <Progress value={((leaveBalance.annual.total - leaveBalance.annual.used) / leaveBalance.annual.total) * 100} className="h-2 bg-slate-700" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-400">Sick Leave</span>
                <span className="text-white">{leaveBalance.sick.total - leaveBalance.sick.used} / {leaveBalance.sick.total} days</span>
              </div>
              <Progress value={((leaveBalance.sick.total - leaveBalance.sick.used) / leaveBalance.sick.total) * 100} className="h-2 bg-slate-700" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-400">Personal Leave</span>
                <span className="text-white">{leaveBalance.personal.total - leaveBalance.personal.used} / {leaveBalance.personal.total} days</span>
              </div>
              <Progress value={((leaveBalance.personal.total - leaveBalance.personal.used) / leaveBalance.personal.total) * 100} className="h-2 bg-slate-700" />
            </div>
            <Button variant="outline" size="sm" className="w-full border-slate-600 text-slate-300 hover:bg-slate-700 mt-2">
              <Plus className="w-4 h-4 mr-2" />
              Apply for Leave
            </Button>
          </CardContent>
        </Card>

        {/* Attendance This Month */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-400" />
              Attendance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <div className="text-4xl font-bold text-white mb-1">
                {attendance.thisMonth.present}/{attendance.thisMonth.total}
              </div>
              <p className="text-slate-400 text-sm">Days Present This Month</p>
              <div className="mt-4 flex justify-center gap-4 text-sm">
                <div className="text-center">
                  <div className="text-green-400 font-semibold">{Math.round((attendance.thisMonth.present / attendance.thisMonth.total) * 100)}%</div>
                  <div className="text-slate-500">Attendance</div>
                </div>
                <div className="text-center">
                  <div className="text-blue-400 font-semibold">{attendance.thisMonth.total - attendance.thisMonth.present}</div>
                  <div className="text-slate-500">Absences</div>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-700 pt-4 mt-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Today's Check-in</span>
                <span className="text-white">{attendance.checkInTime}</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-slate-400">Check-out</span>
                <span className="text-slate-500">{attendance.checkOutTime}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Payslips */}
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              Recent Payslips
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentPayslips.map((payslip, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors">
                <div>
                  <div className="text-white text-sm font-medium">{payslip.month}</div>
                  <div className="text-slate-400 text-xs">{payslip.amount}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500/20 text-green-400 border-0 text-xs">{payslip.status}</Badge>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-white">
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full border-slate-600 text-slate-300 hover:bg-slate-700">
              View All Payslips
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Pending Requests */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-yellow-400" />
            My Pending Requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingRequests.length > 0 ? (
            <div className="space-y-3">
              {pendingRequests.map((request, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-slate-700/30">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(request.status)}
                    <div>
                      <div className="text-white text-sm font-medium">{request.type}</div>
                      <div className="text-slate-400 text-xs">{request.description}</div>
                    </div>
                  </div>
                  {getStatusBadge(request.status)}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No pending requests</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Info Card */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-cyan-400" />
            My Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-lg bg-slate-700/30">
              <div className="text-slate-400 text-xs mb-1">Employee ID</div>
              <div className="text-white font-medium">{myInfo.employeeId}</div>
            </div>
            <div className="p-3 rounded-lg bg-slate-700/30">
              <div className="text-slate-400 text-xs mb-1">Department</div>
              <div className="text-white font-medium">{myInfo.department}</div>
            </div>
            <div className="p-3 rounded-lg bg-slate-700/30">
              <div className="text-slate-400 text-xs mb-1">Position</div>
              <div className="text-white font-medium">{myInfo.position}</div>
            </div>
            <div className="p-3 rounded-lg bg-slate-700/30">
              <div className="text-slate-400 text-xs mb-1">Join Date</div>
              <div className="text-white font-medium">{myInfo.joinDate}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
